import { useCallback, useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Filter, Download, Plus, CheckCircle2, FileWarning, FileQuestion, Minus } from 'lucide-react';
import { cpdAPI, errMsg, download, ACT_STATUS } from '../../lib/cpdRegisterAPI';
import { formatAUDate } from '../../lib/dateUtils';
import { toast } from 'sonner';

const EV = { Present: [CheckCircle2, 'text-emerald-700'], Missing: [FileWarning, 'text-rose-700'], Incomplete: [FileQuestion, 'text-amber-700'], 'Not required': [Minus, 'text-slate-400'] };
export const EvidenceCell = ({ s }) => { const [I, c] = EV[s] || EV['Not required']; return <span className={`inline-flex items-center gap-1 text-xs ${c}`}><I className="w-3.5 h-3.5" />{s}</span>; };

export const CPDActivitiesTab = ({ me, userId, status, onOpen, onAdd, refreshKey, showStaff }) => {
  const [rows, setRows] = useState({ items: [], total: 0 });
  const [q, setQ] = useState('');
  const [fl, setFl] = useState({ status: status || '' });
  const [drawer, setDrawer] = useState(false);
  const [page, setPage] = useState(1);
  const params = { ...Object.fromEntries(Object.entries(fl).filter(([, v]) => v)), q: q || undefined, user_id: userId, page, page_size: 25 };
  const load = useCallback(() => cpdAPI.activities(params).then(setRows).catch((e) => toast.error(errMsg(e))), [JSON.stringify(params)]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const exportCsv = async () => { try { download(await cpdAPI.exportCsv({ ...params, page: undefined }), 'cpd-activities.csv'); } catch (e) { toast.error(errMsg(e)); } };
  const F = (k, label, items) => (
    <div><Label>{label}</Label><Select value={fl[k] || 'all'} onValueChange={(v) => setFl({ ...fl, [k]: v === 'all' ? '' : v })}>
      <SelectTrigger data-testid={`cpd-filter-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">All</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
  );
  return (
    <div className="space-y-4" data-testid="cpd-activities-tab">
      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="Search title, provider, description" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="max-w-xs" data-testid="cpd-search" />
        <Button variant="outline" onClick={() => setDrawer(true)} data-testid="cpd-filters-btn"><Filter className="w-4 h-4 mr-1" /> Filters</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="cpd-export-csv"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        <span className="text-sm text-muted-foreground ml-auto" data-testid="cpd-activity-count">{rows.total} activities</span>
        {onAdd && me.tier !== 'auditor' && <Button onClick={onAdd} className="rounded-full" data-testid="cpd-add-activity-btn-list"><Plus className="w-4 h-4 mr-1" />Add</Button>}
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead>{showStaff && <TableHead>Staff</TableHead>}<TableHead>Activity</TableHead><TableHead>Provider</TableHead>
            <TableHead>Categories</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Evidence</TableHead><TableHead>Frameworks</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No activities yet.</TableCell></TableRow>}
            {rows.items.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(r.id)} data-testid={`cpd-row-${r.id}`}>
                <TableCell className="whitespace-nowrap">{formatAUDate(r.date)}</TableCell>{showStaff && <TableCell>{r.user_name}</TableCell>}
                <TableCell className="font-medium">{r.title}</TableCell><TableCell>{r.provider}</TableCell>
                <TableCell className="text-xs">{r.categories.join(', ')}</TableCell><TableCell className="text-right">{r.hours}</TableCell>
                <TableCell><EvidenceCell s={r.evidence_status} /></TableCell><TableCell className="text-xs">{r.frameworks.join(', ')}</TableCell>
                <TableCell className="text-xs">{ACT_STATUS[r.status]}</TableCell>
              </TableRow>))}
          </TableBody>
        </Table>
      </Card>
      {rows.total > 25 && <div className="flex gap-2 justify-end"><Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
        <Button size="sm" variant="outline" disabled={page * 25 >= rows.total} onClick={() => setPage(page + 1)}>Next</Button></div>}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent data-testid="cpd-filter-drawer"><SheetHeader><SheetTitle>Filter activities</SheetTitle></SheetHeader>
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-2"><div><Label>From</Label><Input type="date" value={fl.date_from || ''} onChange={(e) => setFl({ ...fl, date_from: e.target.value })} /></div>
              <div><Label>To</Label><Input type="date" value={fl.date_to || ''} onChange={(e) => setFl({ ...fl, date_to: e.target.value })} /></div></div>
            {F('status', 'Status', Object.entries(ACT_STATUS))}
            {F('category', 'Category', me.categories.map((c) => [c, c]))}
            {F('provider', 'Provider', me.providers.map((c) => [c, c]))}
            {F('evidence', 'Evidence', Object.keys(EV).map((c) => [c, c]))}
            <Button variant="outline" className="w-full" onClick={() => setFl({})} data-testid="cpd-filter-clear">Clear filters</Button>
          </div></SheetContent>
      </Sheet>
    </div>
  );
};
